import { ReactNode } from "react";
import { Box, styled, Typography } from "@mui/material";

type Variant = "warning" | "success" | "neutral" | "danger";

type VariantProps = {
  colorVariant: Variant;
};

const shouldForwardProp = (prop: PropertyKey) => prop !== "colorVariant";

const Wrapper = styled("div", { shouldForwardProp })<VariantProps>(
  ({ theme, colorVariant }) => ({
    borderRadius: theme.spacing(1),
    borderWidth: 1,
    borderStyle: "solid",
    ...(colorVariant == "neutral"
      ? {
          borderColor: theme.vars.palette.borders.neutral.subtle,
          backgroundColor: theme.vars.palette.backgrounds.neutral.cold.default,
        }
      : {
          borderColor: theme.vars.palette.borders[colorVariant].default,
          backgroundColor:
            theme.vars.palette.backgrounds[colorVariant].moderate.default,
        }),
  }),
);

const Header = styled("div", { shouldForwardProp })<VariantProps>(
  ({ theme, colorVariant }) => ({
    paddingInline: theme.spacing(1.5),
    paddingBlock: theme.spacing(1),
    borderWidth: 1,
    borderBottomStyle: "solid",
    borderColor: theme.vars.palette.borders[colorVariant].default,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    whiteSpace: "nowrap",
    gap: theme.spacing(1.5),
  }),
);

const Tag = styled("div", { shouldForwardProp })<VariantProps>(
  ({ theme, colorVariant }) => ({
    marginTop: theme.spacing(0.5),
    borderRadius: theme.spacing(1),
    paddingInline: theme.spacing(1),
    paddingBlock: theme.spacing(0.25),
    width: "fit-content",
    backgroundColor:
      colorVariant == "neutral"
        ? theme.vars.palette.backgrounds.neutral.moderate.default
        : theme.vars.palette.backgrounds[colorVariant].soft.default,
  }),
);

const pxToEm = (px: number) => `${px / 16}em`;

type LiveMonitorCardProps = {
  roomName: string;
  subject: string;
  movement?: ReactNode;
  status: string;
  lastData: string;
  variant: Variant;
};

export function LiveMonitorCard({
  roomName,
  subject,
  movement,
  status,
  lastData,
  variant,
}: LiveMonitorCardProps) {
  const subtle = `${variant}.subtle`;
  const normal = `${variant}.default`;

  return (
    <Wrapper colorVariant={variant}>
      <Header colorVariant={variant}>
        <Typography
          color={normal}
          variant={"bodySmallMedium"}
          sx={{ flexShrink: 0, fontSize: pxToEm(14) }}
        >
          {roomName}
        </Typography>
        <Typography
          color={subtle}
          variant={"bodyMicroRegular"}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
            textAlign: "end",
            fontSize: pxToEm(12),
          }}
          title={subject}
        >
          {subject}
        </Typography>
      </Header>
      <Box padding={1.5}>
        <Box display={"flex"}>
          <Box flexGrow={1}>
            <Typography
              color={normal}
              variant={"bodySmallMedium"}
              sx={{ fontSize: pxToEm(14) }}
            >
              {status}
            </Typography>
            <Tag colorVariant={variant}>
              <Typography
                color={subtle}
                variant={"bodyMicroRegular"}
                sx={{ fontSize: pxToEm(12) }}
              >
                {lastData}
              </Typography>
            </Tag>
          </Box>
        </Box>
        <Typography
          color={subtle}
          variant={"bodyMicroRegular"}
          marginTop={1}
          minHeight={pxToEm(16)}
          sx={{ fontSize: pxToEm(12) }}
        >
          {movement}
        </Typography>
      </Box>
    </Wrapper>
  );
}
